<template>
    <div>
        <h6><b>Non-Profile Medications</b></h6>
        <div v-show="!disabled">
            <b-row>
                <b-col md="6" cols="12">
                    <search-component v-model="drug" label="Drug" search-u-r-l="/Drug" :params="{ onlyActive: false }"
                        :disabled="disabled" clearable />
                    <b-row>
                        <b-col>
                            <fg-input label="Start Date *">
                                <el-date-picker v-model="startDate" format="MM/dd/yyyy" type="date" :disabled="disabled" />
                            </fg-input>
                        </b-col>
                        <b-col>
                            <fg-input label="End Date">
                                <el-date-picker v-model="endDate" format="MM/dd/yyyy" type="date" :disabled="disabled"
                                    :picker-options="datePickerOptions(startDate)" />
                            </fg-input>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col md="6" cols="12" class="d-flex align-items-start">
                    <fg-input label="Notes" class="flex-fill">
                        <textarea v-model="notes" class="form-control" rows="4" :disabled="disabled"
                            placeholder="Add notes about this medication"></textarea>
                    </fg-input>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button variant="success" @click="addDrug" :disabled="disabled">Add Medication</b-button>
                </b-col>
            </b-row>
        </div>
        <b-row>
            <b-col>
                <b-table ref="other-medication-table" :items="otherMedications" :fields="tableFields"
                    select-mode="single" selectable selected-variant="" no-select-on-click>
                    <template #cell(drugName)="row">
                        <div v-if="row.rowSelected">
                            <div class="mb-2">
                                <strong>{{ row.item.drugName || 'No drug specified' }}</strong>
                            </div>
                            <fg-input label="Notes" class="mb-0">
                                <textarea v-model="row.item.notes" class="form-control textarea-autosize" rows="2" :disabled="disabled"></textarea>
                            </fg-input>
                        </div>
                        <div v-else>
                            <div v-if="row.item.drugName">
                                {{ row.item.drugName }}
                                <div v-if="row.item.notes" class="text-muted small"><strong>{{ row.item.notes }}</strong></div>
                            </div>
                            <div v-else-if="row.item.notes">{{ row.item.notes }}</div>
                            <span v-else class="text-muted font-italic">No information provided</span>
                        </div>
                    </template>
                    <template #cell(endDate)="row">
                        <fg-input v-if="row.rowSelected" class="mb-0">
                            <el-date-picker v-model="row.item.endDate" format="MM/dd/yyyy" type="date"
                                :picker-options="datePickerOptions(row.item.startDate)" :disabled="disabled" />
                        </fg-input>
                        <div v-else>
                            {{ row.value }}
                        </div>
                    </template>
                    <template #cell(edit)="row">
                        <div class="text-right table-icon-actions">
                            <template v-if="row.rowSelected">
                                <a v-b-tooltip.hover title="Save" href="#" @click.prevent="edit(row)" key="save-btn">
                                    <b-icon icon="save" variant="primary" />
                                </a>
                                <a v-b-tooltip.hover title="Cancel" href="#" v-if="row.rowSelected"
                                    @click.prevent="cancelEdit(row)">
                                    <b-icon icon="x-lg" variant="" />
                                </a>
                            </template>
                            <a v-b-tooltip.hover title="Edit" v-else href="#" @click.prevent="edit(row)" key="edit-btn">
                                <b-icon icon="pencil-square" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>

    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Ref, Watch } from 'vue-property-decorator';
import SearchComponent from "@/components/SearchComponent.vue";
import { Patient } from "@/models/Patient";
import axios from "axios";
import { OtherMedication, OtherMedicationViewModel } from "@/models/Drug/OtherMedication";
import { DatePicker } from "element-ui";
import { Drug } from "@/models/Drug/Drug";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { BTable, BvTableFieldArray } from "bootstrap-vue";
import moment from "moment";
// Element UI DatePickerOptions type - defined inline
interface DatePickerOptions {
  disabledDate?: (date: Date) => boolean;
}
import { toRaw } from 'vue';

@Component({
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
    }
})
export default class OtherMedicationsComponent extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ default: false, type: Boolean }) disabled!: boolean;
    @Prop({ default: false, type: Boolean }) onlyActive!: boolean;

    @Ref("other-medication-table") medTable!: BTable;

    protected otherMedications: OtherMedicationViewModel[] = [];
    protected startDate = new Date();
    protected endDate: Date | null = null;
    protected drug: Drug = new Drug();
    protected notes: string = '';

    mounted() {
        this.fetchOtherMedications();
    }

    protected selectedOtherMedication: OtherMedicationViewModel | null = null;

    async edit(row: any) {
        try {
            if (row.rowSelected) {
                row.unselectRow();
                if (this.patient?.id) {
                    await axios.post("/api/OtherMedication", row.item);
                    this.fetchOtherMedications();
                }
            } else {
                this.selectedOtherMedication = structuredClone(toRaw(row.item)) as OtherMedicationViewModel;
                this.medTable.clearSelected();
                row.selectRow();
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async cancelEdit(row: any) {
        row.item.endDate = this.selectedOtherMedication?.endDate;
        row.item.notes = this.selectedOtherMedication?.notes;
        row.unselectRow();
    }

    async addDrug() {
        // Validate: either drug or notes must be provided
        if (!this.drug.drugId && !this.notes?.trim()) {
            this.$notification(NotificationOptions.error("Please provide either a drug or notes"));
            return;
        }

        // Validate: start date is required
        if (!this.startDate) {
            this.$notification(NotificationOptions.error("Start date is required"));
            return;
        }

        if (!this.startDate) {
            this.$notification(NotificationOptions.error("Start Date is required"));
            return;
        }

        if (!this.patient?.id) {
            this.otherMedications.push({
                drugId: this.drug.drugId || undefined,
                drugName: this.drug.productNameShort || undefined,
                source: this.drug.drugId ? this.drug.source : undefined,
                startDate: this.startDate,
                endDate: this.endDate,
                notes: this.notes || undefined
            } as OtherMedicationViewModel);
            this.drug = new Drug();
            this.notes = '';
            return;
        }

        const otherMed = {
            drugId: this.drug.drugId || undefined,
            source: this.drug.drugId ? this.drug.source : undefined,
            patientId: this.patient.id,
            startDate: this.startDate,
            endDate: this.endDate,
            notes: this.notes || undefined
        } as OtherMedication;

        this.addDrugToPatient(otherMed);
    }

    async addDrugToPatient(otherMed: OtherMedication) {
        try {
            await axios.post(`/api/OtherMedication`, otherMed);
            this.drug = new Drug();
            this.notes = '';
            this.fetchOtherMedications();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async commitAllForPatient(patientId: number) {
        await Promise.all(this.otherMedications.map(om =>
            axios.post(`/api/OtherMedication`, {
                ...om,
                patientId: patientId
            })
        ));
    }

    datePickerOptions(startDate: any): DatePickerOptions {
        return {
            disabledDate(date) {
                return moment(date).isBefore(startDate);
            }
        };
    }

    get tableFields(): BvTableFieldArray {
        return [
            { key: "drugName", label: "Drug" },
            { key: "startDate", label: "Start Date", formatter: (value: any) => moment(value).format("L") },
            { key: "endDate", label: "End Date", formatter: (value: any) => value ? moment(value).format("L") : "", class: "end-date-col" },
            { key: "edit", label: "", class: this.disabled ? "d-none" : "edit-col", },
        ];
    }

    async fetchOtherMedications() {
        if (!this.patient?.id) {
            this.otherMedications = [];
            return;
        }
        try {
            const resp = await axios.get<OtherMedicationViewModel[]>("/api/OtherMedication", { params: { patientId: this.patient.id } });
            if (!this.onlyActive) {
                this.otherMedications = resp.data;
                return;
            }
            this.otherMedications = resp.data.filter(d => moment(d.startDate).isSameOrBefore() && (!d.endDate || moment(d.endDate).isAfter()));
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
            this.otherMedications = [];
        }
    }

    @Watch("patient")
    patientChanged() {
        this.drug = new Drug();
        this.notes = '';
        this.fetchOtherMedications();
    }

    @Watch("drug")
    drugChanged(newValue: Drug | null) {
        // Handle when the search component is cleared (X button clicked)
        if (!newValue || !newValue.drugId) {
            this.drug = null;
            this.startDate = new Date();
            this.endDate = null;
        }
    }
}
</script>

<!--suppress CssUnusedSymbol -->
<style scoped>
::v-deep(.end-date-col) {
    width: 200px;
}

::v-deep(.edit-col) {
    width: 40px;
}

::v-deep(th) {
    font-weight: normal !important;
}
</style>
